import { Chip } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { ValidationLevel } from '@/api/graphql';
import { getValidationColor } from '@/utils';

interface Props {
  level?: ValidationLevel | null;
  count?: number;
  /**
   * Makes the chip a shortcut to the findings it counts. MUI turns a chip with a handler into a real
   * button — hover, pointer cursor and keyboard activation — so the summary reads as clickable
   * without any styling of its own. Omitted where the chip is only a label.
   */
  onClick?: () => void;
}

/**
 * Worst-severity summary for an item. "Clean" gets a chip of its own rather than an empty cell — in a
 * grid, absence is ambiguous between "no problems" and "not checked".
 */
export default function ValidationChip({ level, count, onClick }: Props) {
  const color = getValidationColor(level);
  const label =
    level === ValidationLevel.Error
      ? `${count ?? ''} error${count === 1 ? '' : 's'}`.trim()
      : level === ValidationLevel.Warning
      ? `${count ?? ''} warning${count === 1 ? '' : 's'}`.trim()
      : 'Clean';

  const Icon =
    level === ValidationLevel.Error
      ? ErrorOutlineIcon
      : level === ValidationLevel.Warning
      ? WarningAmberIcon
      : CheckCircleOutlineIcon;

  return (
    <Chip
      size="small"
      variant="outlined"
      icon={<Icon sx={{ color: `${color} !important` }} />}
      label={label}
      onClick={onClick}
      sx={{ color, borderColor: color, minWidth: 110 }}
    />
  );
}
